import React, { useState, useEffect } from 'react';
import CommonSection from '../components/Ui/CommonSection';
import Helmet from '../components/Helmet/Helmet';
import { Container, Row, Col, Form, FormGroup } from 'react-bootstrap';
import '../styles/checkout.css';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import useAuth from '../custom-hooks/useAuth';
import { cartActions } from '../redux/slices/cartSlice';
import { createOrder } from '../services/orderService';
import { validateCoupon } from '../services/couponService';
import ConfirmModal from '../components/Ui/ConfirmModal';
import { getProductImg, handleImgError } from '../utils/imageHelper';

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.config';

const Checkout = () => {
  const { currentUser } = useAuth();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const totalQty = useSelector((state) => state.cart.totalQuantity);
  const totalAmount = useSelector((state) => state.cart.totalAmount);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isOrderConfirmOpen, setIsOrderConfirmOpen] = useState(false);

  // VIP Promo Voucher State
  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);

  const finalDueAmount = Math.max(0, totalAmount - discountAmount);

  const handleApplyCoupon = async (e) => {
    if (e) e.preventDefault();
    if (!couponCodeInput.trim()) {
      toast.warning('Please enter a voucher code');
      return;
    }
    setCouponLoading(true);
    try {
      const res = await validateCoupon(couponCodeInput, totalAmount);
      if (res.valid) {
        setAppliedCoupon(res.coupon);
        setDiscountAmount(res.discountAmount);
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Failed to validate voucher');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCodeInput('');
    toast.info('Voucher privilege removed');
  };

  const [formData, setFormData] = useState(() => {
    const savedFormData = localStorage.getItem('checkoutFormData');
    return savedFormData
      ? JSON.parse(savedFormData)
      : {
        name: '',
        email: '',
        phoneNumber: '',
        address: '',
        city: '',
        postalCode: '',
        country: '',
      };
  });

  const { name, email, phoneNumber, address, city, postalCode, country } = formData;

  // Auto-fill user name, email, saved address and phone number from profile
  useEffect(() => {
    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || currentUser.displayName || '',
        email: prev.email || currentUser.email || '',
      }));

      // If address or phone are missing, pull from Firestore user profile
      const fetchSavedProfile = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setFormData((prev) => ({
              ...prev,
              phoneNumber: prev.phoneNumber || data.phoneNumber || '',
              address: prev.address || data.address || '',
              city: prev.city || data.city || '',
              postalCode: prev.postalCode || data.postalCode || '',
              country: prev.country || data.country || 'Egypt',
            }));
          }
        } catch (e) {
          // fallback quietly
        }
      };

      fetchSavedProfile();
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    localStorage.setItem('checkoutFormData', JSON.stringify(formData));
  }, [formData]);

  const handlePlaceOrder = (e) => {
    if (e) e.preventDefault();

    if (totalQty === 0) {
      toast.error('Your cart is empty');
      return navigate('/shop');
    }

    if (!currentUser) {
      toast.info('Please sign in to complete your purchase');
      return navigate('/login', { state: { from: '/checkout' } });
    }

    if (
      !name.trim() ||
      !email.trim() ||
      !phoneNumber.trim() ||
      !address.trim() ||
      !city.trim() ||
      !postalCode.trim() ||
      !country.trim()
    ) {
      toast.error('Please fill in all shipping details');
      return;
    }

    // Open confirmation modal to confirm order placement
    setIsOrderConfirmOpen(true);
  };

  const executeOrderPlacement = async () => {
    setLoading(true);

    try {
      const order = await createOrder({
        userId: currentUser.uid,
        userEmail: currentUser.email || email.trim(),
        customerName: name.trim(),
        phoneNumber: phoneNumber.trim(),
        shippingAddress: {
          address: address.trim(),
          city: city.trim(),
          postalCode: postalCode.trim(),
          country: country.trim(),
        },
        cartItems,
        totalAmount: finalDueAmount,
        totalQty,
        paymentMethod: 'Cash on Delivery',
        couponCode: appliedCoupon ? appliedCoupon.code : null,
        discountAmount: discountAmount || 0,
      });

      // Clear Redux Cart & Local Storage Cart
      dispatch(cartActions.clearCart());

      setIsOrderConfirmOpen(false);
      toast.success(`Order placed successfully! Reference: ${order.orderNumber}`, {
        autoClose: 3500,
      });

      setLoading(false);
      navigate('/orders');
    } catch (error) {
      setLoading(false);
      console.error('Failed to create order:', error);
      toast.error(error.message || 'Failed to place order. Please try again.');
    }
  };

  return (
    <Helmet title="Checkout | L'HABILLEUR">
      <CommonSection title="Checkout" />
      <section className="checkout__section py-5">
        <Container>
          <Row className="g-4">
            <Col lg="7" md="12">
              <div className="checkout__billing-card">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h4 className="fw-bold m-0" style={{ color: 'var(--primary-color)' }}>
                    Shipping & Delivery Details
                  </h4>
                  <span className="badge bg-light text-dark border px-3 py-2">
                    <i className="ri-truck-line me-1 text-primary"></i> Free Express Delivery
                  </span>
                </div>

                <Form className="billing__form" onSubmit={handlePlaceOrder}>
                  <Row className="g-3">
                    <Col md="6">
                      <FormGroup className="form__group">
                        <label className="form-label fw-semibold text-muted small">Full Name</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. John Doe"
                          name="name"
                          value={name}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="form__group">
                        <label className="form-label fw-semibold text-muted small">Email Address</label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="e.g. john@example.com"
                          name="email"
                          value={email}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="form__group">
                        <label className="form-label fw-semibold text-muted small">Phone Number</label>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="e.g. +1 555 123 4567"
                          name="phoneNumber"
                          value={phoneNumber}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="form__group">
                        <label className="form-label fw-semibold text-muted small">Country</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. United States"
                          name="country"
                          value={country}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup className="form__group">
                        <label className="form-label fw-semibold text-muted small">Street Address</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Apartment, suite, unit, building, floor"
                          name="address"
                          value={address}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="form__group">
                        <label className="form-label fw-semibold text-muted small">City</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. New York"
                          name="city"
                          value={city}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="form__group">
                        <label className="form-label fw-semibold text-muted small">Postal Code / Zip</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. 10001"
                          name="postalCode"
                          value={postalCode}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="checkout__payment-method mt-4 p-3 rounded border">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="radio"
                        id="cod"
                        name="paymentMethod"
                        defaultChecked
                        className="form-check-input mt-0"
                      />
                      <label htmlFor="cod" className="form-check-label fw-bold mb-0 cursor-pointer">
                        Cash on Delivery (COD)
                      </label>
                    </div>
                    <p className="text-muted small mt-2 mb-0 ms-4">
                      Pay with cash when your luxury parcel arrives at your doorstep. Verified inspection before payment.
                    </p>
                  </div>
                </Form>
              </div>
            </Col>

            <Col lg="5" md="12">
              <div className="checkout__summary-card">
                <h5 className="fw-bold mb-3 pb-2 border-bottom text-white">Order Summary</h5>

                {/* Items preview list */}
                <div className="checkout__items-list mb-3">
                  {cartItems.length > 0 ? (
                    cartItems.map((item) => (
                      <div key={item.id} className="checkout__item-row d-flex align-items-center gap-3 py-2">
                        <img
                          src={getProductImg(item)}
                          onError={(e) => handleImgError(e, item)}
                          alt={item.productName}
                          className="checkout__item-img rounded"
                        />
                        <div className="flex-grow-1 min-w-0">
                          <h6 className="checkout__item-title text-white mb-0 text-truncate">
                            {item.productName}
                          </h6>
                          <span className="text-white-50 small">
                            Qty: {item.quantity} × ${item.price}
                          </span>
                        </div>
                        <span className="text-white fw-bold">${item.totalPrice}</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-white-50 py-3 text-center">Your cart is empty</div>
                  )}
                </div>

                {/* VIP Privilege Voucher Input */}
                <div className="checkout__coupon-box p-3 rounded mb-3" style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px dashed rgba(212, 175, 55, 0.4)' }}>
                  <label className="text-warning small fw-bold mb-2 d-flex align-items-center gap-1">
                    <i className="ri-coupon-3-line"></i> VIP Privilege Voucher / Promo Code
                  </label>
                  {!appliedCoupon ? (
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm text-uppercase fw-semibold"
                        placeholder="e.g. ATELIER10, LUXURY50"
                        value={couponCodeInput}
                        onChange={(e) => setCouponCodeInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon(e)}
                        style={{ background: 'rgba(0, 0, 0, 0.3)', color: '#fff', borderColor: 'rgba(255, 255, 255, 0.2)' }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-warning text-nowrap px-3"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                      >
                        {couponLoading ? 'Checking...' : 'Apply'}
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-between p-2 rounded" style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                      <div>
                        <span className="badge bg-success me-2">{appliedCoupon.code}</span>
                        <span className="text-white small fw-bold">-${discountAmount} Off</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm text-danger p-0 border-0"
                        onClick={handleRemoveCoupon}
                        title="Remove code"
                      >
                        <i className="ri-close-circle-fill fs-5"></i>
                      </button>
                    </div>
                  )}
                  <small className="text-white-50 d-block mt-1" style={{ fontSize: '0.72rem' }}>
                    Exclusive codes: <strong>ATELIER10</strong> (10% off), <strong>LUXURY50</strong> ($50 off &gt;$300)
                  </small>
                </div>

                <div className="checkout__totals border-top pt-3">
                  <div className="d-flex justify-content-between text-white-50 mb-2">
                    <span>Total Quantity:</span>
                    <span className="text-white">{totalQty} items</span>
                  </div>
                  <div className="d-flex justify-content-between text-white-50 mb-2">
                    <span>Subtotal:</span>
                    <span className="text-white">${totalAmount}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="d-flex justify-content-between text-warning mb-2 fw-semibold">
                      <span>Voucher Discount ({appliedCoupon?.code}):</span>
                      <span>-${discountAmount}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between text-white-50 mb-3">
                    <span>Express Delivery:</span>
                    <span className="text-success fw-bold">FREE</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-3 border-top mb-4">
                    <h5 className="text-white fw-bold mb-0">Total Due:</h5>
                    <div className="text-end">
                      {discountAmount > 0 && (
                        <span className="text-white-50 text-decoration-line-through small d-block">
                          ${totalAmount}
                        </span>
                      )}
                      <h4 className="text-warning fw-bolder mb-0">${finalDueAmount}</h4>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handlePlaceOrder}
                    disabled={loading || totalQty === 0}
                    className="checkout__order-btn w-100"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line ri-spin me-2"></i> Placing Order...
                      </>
                    ) : (
                      <>
                        Place Order (${finalDueAmount}) <i className="ri-arrow-right-line ms-1"></i>
                      </>
                    )}
                  </button>

                  <div className="text-center mt-3">
                    <Link to="/cart" className="text-white-50 small text-decoration-underline">
                      ← Modify Cart Items
                    </Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Order Placement Confirmation Modal */}
      <ConfirmModal
        isOpen={isOrderConfirmOpen}
        onClose={() => !loading && setIsOrderConfirmOpen(false)}
        onConfirm={executeOrderPlacement}
        title="Confirm Order Commission"
        badge="Final Confirmation"
        message="Please review your delivery destination and total before finalizing your order placement with the Atelier."
        confirmText="Confirm & Place Order"
        cancelText="Review Details"
        confirmVariant="primary"
        icon="ri-checkbox-circle-line"
        isLoading={loading}
      >
        <div className="confirm-modal__details-card">
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted small">Items Total:</span>
            <strong className="text-dark small">{totalQty} piece{totalQty > 1 ? 's' : ''} (${totalAmount})</strong>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted small">Deliver To:</span>
            <strong className="text-dark small text-end" style={{ maxWidth: '240px' }}>
              {name.trim()} &bull; {address.trim()}, {city.trim()}
            </strong>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted small">Phone:</span>
            <strong className="text-dark small">{phoneNumber.trim()}</strong>
          </div>
          <div className="d-flex justify-content-between border-top pt-2">
            <span className="text-muted small">Payment Method:</span>
            <span className="badge bg-success small">Cash on Delivery (Free Delivery)</span>
          </div>
        </div>
      </ConfirmModal>
    </Helmet>
  );
};

export default Checkout;